/* eslint-disable consistent-return */
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useMemo, useState } from 'react';
import { withRouter, Link } from 'react-router-dom';
import { Form, Modal, Button, Row, Col, message } from 'antd';
import { Segment } from '@fxtr/i18n';

import config from '$config';
import { AddressSelectorWithGooglePlaces } from '$app/shared/components/AddressSelectorWithGooglePlaces';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import {
  normalizePhoneFields,
  HERO_MOBILE_FIELD_NAMES,
} from '$app/shared/utils/phoneValidation';
import makeHeroesForm from '../HeroesForm';
import PostcodeLookup from '../../components/PostcodeLookup';
import LicensePhotoUpload from '../LicensePhotoUpload';
import ListLicensePhotos from '../ListLicensePhotos';
import {
  fetchDriverZones,
  fetchHero,
  fetchHeroAddresses,
  getHeroesErrorMessage,
  saveHeroAddress,
  updateHero,
} from '../../heroesApi';

const DRIVING_LICENSE_FRONT = 'dl-front';
const DRIVING_LICENSE_BACK = 'dl-back';

const mapPropsToFields = (props) => {
  const { initialValue } = props;
  if (!initialValue) return;
  const keys = Object.keys(initialValue);
  return keys.reduce((ac, val) => {
    ac[val] = Form.createFormField({
      value: initialValue[val],
    });
    return ac;
  }, {});
};

const HeroesForm = makeHeroesForm(mapPropsToFields);

function HeroesEditPage({ match }) {
  const { heroId } = match.params;
  const [hero, setHero] = useState(null);
  const [zones, setZones] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const [homeAddress, setHomeAddress] = useState(null);

  useEffect(() => {
    let isMounted = true;

    const loadPage = async () => {
      try {
        const [nextHero, nextZones] = await Promise.all([
          fetchHero(heroId),
          fetchDriverZones(),
        ]);

        let nextAddresses = [];

        if (nextHero.driverUuid) {
          try {
            nextAddresses = await fetchHeroAddresses(nextHero.driverUuid);
          } catch (error) {
            message.error(getHeroesErrorMessage(error));
          }
        }

        if (isMounted) {
          setHero(nextHero);
          setZones(nextZones);
          setAddresses(nextAddresses);
        }
      } catch (error) {
        message.error(getHeroesErrorMessage(error));
        if (isMounted) {
          setHero(null);
          setZones([]);
          setAddresses([]);
        }
      } finally {
        if (isMounted) {
          setPageLoading(false);
        }
      }
    };

    loadPage();

    return () => {
      isMounted = false;
    };
  }, [heroId]);

  const activeAddress = useMemo(
    () => [...addresses].sort((a, b) => b.addressId - a.addressId)[0],
    [addresses]
  );

  const handleSubmit = (event, form) => {
    form.validateFieldsAndScroll(async (err, values) => {
      if (!err) {
        try {
          setLoading(true);
          message.info('Saving hero');
          const savedHero = await updateHero(
            heroId,
            normalizePhoneFields(values, values.segment, HERO_MOBILE_FIELD_NAMES, {
              mobileOnlyFields: HERO_MOBILE_FIELD_NAMES,
            }),
            hero?.rates || []
          );
          setHero(savedHero);
          message.success('Saved hero');
        } catch (error) {
          message.error(getHeroesErrorMessage(error));
        } finally {
          setLoading(false);
        }
      }
    });
  };

  const handleConfirmAddress = async () => {
    if (!homeAddress || !hero?.driverUuid) {
      return;
    }

    try {
      await saveHeroAddress(hero.driverUuid, homeAddress);
      const nextAddresses = await fetchHeroAddresses(hero.driverUuid);
      setAddresses(nextAddresses);
      setShowAddressEditor(false);
      setHomeAddress(null);
      message.success('Saved address');
    } catch (error) {
      message.error(getHeroesErrorMessage(error));
    }
  };

  if (pageLoading) {
    return <Loading />;
  }

  if (!hero || !zones) {
    return null;
  }

  return (
    <Container style={{ height: window.innerHeight }}>
      <Header>
        <Link to="/heroes">Back</Link>
        <Title>
          Edit Hero:{' '}
          <em>
            {hero.firstName} {hero.lastName}
          </em>
        </Title>
      </Header>
      <Row>
        <Col span={16}>
          <HeroesForm
            onSubmit={handleSubmit}
            zones={zones}
            loading={loading}
            initialValue={hero}
          />
        </Col>
        <Col span={8}>
          <h4 style={{ textAlign: 'center' }}>
            Home address
            <span>
              <Button
                type="link"
                style={{ textAlign: 'center' }}
                onClick={() => setShowAddressEditor(true)}
              >
                Edit address
              </Button>
            </span>
          </h4>

          {activeAddress && (
            <a
              target="_blank"
              href={`http://maps.google.com/maps?z=12&t=m&q=loc:${activeAddress.latitude}+${activeAddress.longitude}`}
              rel="noreferrer"
            >
              {activeAddress.display}
            </a>
          )}

          <Modal
            title="Edit home address"
            visible={showAddressEditor}
            onOk={handleConfirmAddress}
            onCancel={() => setShowAddressEditor(false)}
            okText="Save"
            cancelText="Cancel"
          >
            {hero.segment === Segment.Fixter_UK && (
              <PostcodeLookup onSelectAddress={(address) => setHomeAddress(address)} />
            )}
            {hero.segment !== Segment.Fixter_UK && (
              <AddressSelectorWithGooglePlaces
                countryRule={['uk', 'fr']}
                apiKey={`${config.gMaps.apiKey}`}
                onAddressSelected={(address) => setHomeAddress(address)}
              />
            )}
          </Modal>
          <div style={{ marginTop: 20 }}>
            <h4 style={{ textAlign: 'left' }}>Driving License Front</h4>
            <ListLicensePhotos ownerId={hero.driverUuid} side={DRIVING_LICENSE_FRONT} />
            <br />
            <LicensePhotoUpload ownerId={hero.driverUuid} side={DRIVING_LICENSE_FRONT} />
          </div>
          <div style={{ marginTop: 20 }}>
            <h4 style={{ textAlign: 'left' }}>Driving License Back</h4>
            <ListLicensePhotos ownerId={hero.driverUuid} side={DRIVING_LICENSE_BACK} />
            <br />
            <LicensePhotoUpload ownerId={hero.driverUuid} side={DRIVING_LICENSE_BACK} />
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default withRouter(HeroesEditPage);
